import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Radio, TrendingUp, ArrowRight, Sparkles, Bell } from "lucide-react";
import { api, CATEGORIES } from "@/lib/api";
import { ArticleCard } from "@/components/ArticleCard";
import { TrustBadge } from "@/components/TrustBadge";
import { RefreshButton } from "@/components/RefreshButton";
import { AdSlot } from "@/components/AdSlot";
import { SponsoredCard } from "@/components/SponsoredCard";
import { useI18n, formatRelative, formatExact, itemDate } from "@/i18n";
import { HowItWorks } from "@/components/HowItWorks";
import { PulseStrip } from "@/components/PulseStrip";
import { CategoryTag, CategoryIcon } from "@/components/CategoryTag";
import { useAds, sponsoredAt } from "@/lib/ads";

function LiveTicker({ items }) {
  const { lang, title } = useI18n();
  return (
    <div className="flex flex-col divide-y divide-slate-100">
      {items.map((a) => (
        <Link key={a.id} to={`/article/${a.id}`} className="group py-3 flex gap-3 items-start hover:bg-violet-50/40 -mx-2 px-2 rounded transition-colors">
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-violet-500 shrink-0" />
          <div className="min-w-0">
            <p className="text-[13.5px] font-medium text-slate-800 leading-snug line-clamp-2 group-hover:text-violet-800">{title(a)}</p>
            <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
              <span className="font-medium text-slate-600">{a.source_name}</span>
              <span>·</span>
              <span title={formatExact(itemDate(a), lang)}>{formatRelative(itemDate(a), lang)}</span>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

export default function Home() {
  const { t, catName, lang, pick, title } = useI18n();
  const { data: ads } = useAds();

  const { data: top } = useQuery({ queryKey: ["top"], queryFn: async () => (await api.get("/top-stories?limit=5")).data });
  const { data: latest } = useQuery({ queryKey: ["home-latest"], queryFn: async () => (await api.get("/articles?limit=9")).data });
  const { data: trending } = useQuery({ queryKey: ["trending"], queryFn: async () => (await api.get("/trending")).data });
  const { data: cats } = useQuery({ queryKey: ["cats"], queryFn: async () => (await api.get("/categories")).data });
  const { data: live } = useQuery({ queryKey: ["live"], queryFn: async () => (await api.get("/live?limit=8&diverse=true")).data, refetchInterval: 30000 });

  const hero = top?.[0];
  const heroSide = top?.slice(1, 5) || [];

  return (
    <div className="pt-16">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-14 pb-8">
        <div data-testid="live-news-banner" className="inline-flex items-center gap-2 mb-5">
          <span className="live-dot w-2 h-2 rounded-full bg-violet-600" />
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-violet-700">{t("hero.badge")}</span>
        </div>
        <h1 data-testid="hero-title" className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">
          {t("hero.title")}
        </h1>
        <p data-testid="hero-subtitle" className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">{t("hero.subtitle")}</p>
        <p className="mt-2 text-sm text-slate-500 max-w-2xl leading-relaxed">{t("hero.support")}</p>
        <div className="mt-6"><RefreshButton /></div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-8">
            <SectionTitle icon={<TrendingUp size={16} />} title={t("section.topStories")} />
            {hero ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-4">
                <Link to={`/article/${hero.id}`} className="group sm:row-span-2 bg-white rounded-xl border border-slate-200/80 overflow-hidden hover:border-violet-300 hover:shadow-[0_8px_30px_rgba(147,51,234,0.1)] transition-all flex flex-col">
                  {hero.image_url && (
                    <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                      <img src={hero.image_url} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" onError={(e) => (e.currentTarget.parentElement.style.display = "none")} />
                    </div>
                  )}
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <CategoryTag slug={hero.categories?.[0] || "ai-companies"} />
                      <TrustBadge status={hero.status} mode={hero.display_mode} />
                    </div>
                    <h2 className="font-display font-bold text-xl sm:text-2xl leading-tight tracking-tight group-hover:text-violet-800 transition-colors">{title(hero)}</h2>
                    {pick(hero, "summary") && <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-4">{pick(hero, "summary")}</p>}
                    <div className="mt-auto pt-4 flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="font-medium text-slate-700">{hero.source_name}</span>
                      <span>·</span>
                      <span title={formatExact(itemDate(hero), lang)}>{formatRelative(itemDate(hero), lang)}</span>
                    </div>
                  </div>
                </Link>

                {heroSide.map((a) => (
                  <Link key={a.id} to={`/article/${a.id}`} className="group bg-white rounded-xl border border-slate-200/80 p-4 hover:border-violet-300 transition-all flex flex-col">
                    <div className="flex items-center justify-between mb-1.5">
                      <CategoryTag slug={a.categories?.[0] || "ai-companies"} plain />
                      <TrustBadge status={a.status} mode={a.display_mode} />
                    </div>
                    <h3 className="font-display font-semibold text-sm leading-snug tracking-tight group-hover:text-violet-800 line-clamp-3">{title(a)}</h3>
                    <div className="mt-auto pt-2 text-[11px] text-muted-foreground">
                      <span className="font-medium text-slate-600">{a.source_name}</span> · <span title={formatExact(itemDate(a), lang)}>{formatRelative(itemDate(a), lang)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : <EmptyState text={t("section.monitoring")} />}
          </div>

          <aside className="lg:col-span-4">
            <SectionTitle icon={<Radio size={16} />} title={t("section.liveNews")} live />
            <div className="mt-4 bg-white rounded-xl border border-slate-200/80 p-4">
              {live?.items?.length ? <LiveTicker items={live.items} /> : <p className="text-sm text-muted-foreground py-6 text-center">{t("section.monitoring")}</p>}
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground text-center">{t("section.liveMonitoring")}</p>
          </aside>
        </div>
      </section>

      <PulseStrip />

      <HowItWorks />

      {trending?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <SectionTitle icon={<Sparkles size={16} />} title={t("section.trending")} />
          <div className="mt-4 flex flex-wrap gap-2">
            {trending.map((tp) => (
              <Link key={tp.topic} to={`/search?q=${encodeURIComponent(tp.topic)}`} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-700 hover:border-violet-300 hover:text-violet-700 transition-colors">
                {tp.topic} <span className="font-mono text-[11px] text-violet-500">{tp.count}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Advertising placement (clearly labelled) */}
      <AdSlot />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <SectionTitle title={t("section.latest")} />
          <div className="flex items-center gap-3">
            <RefreshButton />
            <Link to="/latest" className="text-sm font-medium text-violet-700 inline-flex items-center gap-1 hover:gap-1.5 transition-all">{t("section.viewAll")} <ArrowRight size={14} /></Link>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-5">
          {latest?.items?.map((a, i) => (
            <React.Fragment key={a.id}>
              {sponsoredAt(i, ads) !== undefined && <SponsoredCard card={sponsoredAt(i, ads)} />}
              <ArticleCard article={a} index={i} />
            </React.Fragment>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <SectionTitle title={t("section.byCategory")} />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-5">
          {CATEGORIES.filter((c) => c.slug !== "latest").map((c) => {
            const count = cats?.find((x) => x.slug === c.slug)?.count || 0;
            return (
              <Link key={c.slug} to={`/category/${c.slug}`} className="group bg-white border border-slate-200/80 rounded-xl p-4 hover:border-violet-300 hover:bg-violet-50/30 transition-all">
                <span className="inline-flex w-8 h-8 items-center justify-center rounded-lg bg-violet-50 text-violet-700 group-hover:bg-violet-600 group-hover:text-white transition-colors mb-2"><CategoryIcon slug={c.slug} size={16} /></span>
                <div className="font-display font-semibold text-slate-800 group-hover:text-violet-800">{catName(c.slug)}</div>
                <div className="font-mono text-xs text-muted-foreground mt-1">{count} {t("common.stories")}</div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function SectionTitle({ title, icon, live }) {
  return (
    <div className="flex items-center gap-2">
      {live && <span className="live-dot w-2 h-2 rounded-full bg-violet-600" />}
      {icon && <span className="text-violet-600">{icon}</span>}
      <h2 className="font-display font-bold text-lg sm:text-xl tracking-tight">{title}</h2>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="mt-4 bg-white rounded-xl border border-dashed border-slate-300 p-10 text-center">
      <p className="text-sm text-muted-foreground">{text}</p>
    </div>
  );
}
